import React, { JSX } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
  Card,
  Avatar,
  Divider,
  Alert,
  Tooltip,
  TextField as MuiTextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { IoClose } from 'react-icons/io5';
import { Icon } from '@iconify/react';
import * as yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import palette from 'src/theme/palette';
import SelectComponent from './SelectComponent';
import ToastNotifications from './ToastNotifications';
import { transactionService } from 'src/services/transaction.service';
import { invalidateStudentFinancialQueries } from 'src/utils/queryInvalidation';

interface TransactionModalProps {
  open: boolean;
  closeModal: () => void;
  onSubmit?: (values: TransactionFormValues) => Promise<void>;
  studentId?: string;
}

export interface TransactionFormValues {
  selectedStudent:
    | { label: string; value: string; currentPoints: number; email?: string }
    | { label: string; value: string; currentPoints: number; email?: string }[]
    | null;
  reason: { label: string; value: string; points: number; priceType: string; type?: string } | null;
  amount: string;
  description?: string;
  selectAll: boolean;
}

const transactionValidationSchema = yup.object({
  selectedStudent: yup
    .mixed()
    .required('Select at least one student')
    .test('is-valid-selection', 'Please select at least one student', function (value) {
      if (!value) return false;

      // For multi-select case
      if (Array.isArray(value)) {
        return (
          value.length > 0 &&
          value.every(
            (student) =>
              student &&
              typeof student === 'object' &&
              'label' in student &&
              'value' in student &&
              'currentPoints' in student &&
              typeof student.currentPoints === 'number'
          )
        );
      }

      // For single-select case
      return (
        value &&
        typeof value === 'object' &&
        'label' in value &&
        'value' in value &&
        'currentPoints' in value &&
        typeof value.currentPoints === 'number'
      );
    }),
  reason: yup
    .object({
      label: yup.string().required('Reason label is required'),
      value: yup.string().required('Reason value is required'),
      points: yup.number().required('Reason points is required'),
      priceType: yup.string().required('Reason price type is required'),
    })
    .required('Select a transaction reason')
    .nullable(),
  amount: yup
    .string()
    .test('required-or-fixed', 'Enter an amount', function (value, context) {
      const reason = context.parent.reason;
      if (reason?.priceType === 'fixed') {
        return true;
      }
      return Boolean(value);
    })
    .test('valid-amount', 'Amount must be a positive number', function (value, context) {
      const reason = context.parent.reason;
      if (reason?.priceType === 'fixed') {
        return true;
      }
      if (!value) return false;
      return /^\d+$/.test(value) && parseInt(value, 10) > 0;
    }),
  description: yup.string().max(250, 'Notes cannot exceed 250 characters').optional(),
  selectAll: yup.boolean(),
});

export const TransactionModal = ({ open, closeModal, studentId }: TransactionModalProps): JSX.Element => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [typeFilter, setTypeFilter] = React.useState<'ALL' | 'credit' | 'debit'>('ALL');
  const queryClient = useQueryClient();

  // Fetch active students
  const { data: students = [], isLoading: isLoadingStudents } = useQuery({
    queryKey: ['active-students'],
    queryFn: () => transactionService.getActiveStudents(),
  });

  // Fetch transaction types
  const { data: reasons = [], isLoading: isLoadingReasons } = useQuery({
    queryKey: ['transaction-reasons'],
    queryFn: () => transactionService.getAllTransactionTypes(),
  });

  const studentOptions = React.useMemo(
    () =>
      students.map((student) => ({
        label: `${student.name} ($${Number(student.currentPoints || 0).toFixed(2)})`,
        value: student.id,
        currentPoints: Number(student.currentPoints || 0),
        email: student.email || '',
        name: student.name,
      })),
    [students]
  );

  const reasonOptions = React.useMemo(
    () =>
      reasons
        .filter((reason) => {
          if (typeFilter === 'ALL') return true;
          return reason.type === typeFilter;
        })
        .map((reason) => ({
          label: `${reason.name} (${reason.type === 'credit' ? '+' : '-'}${reason.priceType === 'fixed' ? `$${reason.points}` : 'Any amount'})${reason.type === 'debit' && reason.charge_mode === 'bill' ? ' · Bill' : ''}`,
          value: reason.id,
          points: reason.points,
          type: reason.type,
          priceType: reason.priceType,
          chargeMode: reason.charge_mode,
          name: reason.name,
        })),
    [reasons, typeFilter]
  );

  const formik = useFormik<TransactionFormValues>({
    initialValues: {
      selectedStudent: studentId ? studentOptions.find((student) => student.value === studentId) || null : null,
      reason: null,
      amount: '',
      description: '',
      selectAll: false,
    },
    validationSchema: transactionValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: Boolean(studentId),
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const selectedReason = reasons.find((reason) => reason.id === values.reason?.value);

        if (!selectedReason) throw new Error('Invalid transaction type');

        const transactionAmount = Number(values.amount);

        const selectedStudents = values.selectAll
          ? studentOptions
          : Array.isArray(values.selectedStudent)
          ? values.selectedStudent
          : values.selectedStudent
          ? [values.selectedStudent]
          : [];

        if (selectedStudents.length === 0) throw new Error('No students selected');

        // Execute transactions for all selected scholars
        await Promise.all(
          selectedStudents.map(async (student) => {
            const currentPoints = student.currentPoints;

            if (selectedReason.type === 'debit') {
              const willOverdraw = transactionAmount > currentPoints;
              const finalAmount = willOverdraw ? transactionAmount + 35 : transactionAmount;

              await transactionService.createTransaction({
                studentId: student.value,
                transactionTypeId: values.reason?.value || '',
                amount: finalAmount,
                // Leave the note empty when staff wrote none; the student sees the type name ("Monthly Rent").
                description: values.description || undefined,
                reason: selectedReason.name || '',
                extendedProperties: {
                  originalAmount: transactionAmount,
                  overdrawFee: willOverdraw ? 35 : 0,
                  transactionType: 'debit',
                  notes: values.description || undefined,
                },
              });
            } else if (selectedReason.type === 'credit') {
              await transactionService.createTransaction({
                studentId: student.value,
                transactionTypeId: values.reason?.value || '',
                amount: transactionAmount,
                description: values.description || undefined,
                reason: selectedReason.name || '',
                extendedProperties: {
                  originalAmount: transactionAmount,
                  overdrawFee: 0,
                  transactionType: 'credit',
                  notes: values.description || undefined,
                },
              });
            }
          })
        );

        await invalidateStudentFinancialQueries(queryClient);

        ToastNotifications.success('Transaction(s) submitted successfully');
        closeModal();
        formik.resetForm();
      } catch (error) {
        ToastNotifications.error(error instanceof Error ? error.message : 'Failed to submit transaction');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleSelectAllToggle = () => {
    const nextState = !formik.values.selectAll;
    formik.setFieldValue('selectAll', nextState, false);
    if (nextState) {
      formik.setFieldValue('selectedStudent', studentOptions, false);
    } else {
      formik.setFieldValue('selectedStudent', null, false);
    }
    formik.setFieldError('selectedStudent', undefined);
    formik.setFieldTouched('selectedStudent', true, false);
  };

  const handleStudentSelection = (selected: any) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      formik.setFieldValue('selectedStudent', null, false);
      formik.setFieldValue('selectAll', false, false);
      formik.setFieldError('selectedStudent', undefined);
      formik.setFieldTouched('selectedStudent', true, false);
      return;
    }

    const isValidSelection = Array.isArray(selected)
      ? selected.length > 0 &&
        selected.every((s) => s && typeof s === 'object' && 'value' in s && 'label' in s && 'currentPoints' in s)
      : selected &&
        typeof selected === 'object' &&
        'value' in selected &&
        'label' in selected &&
        'currentPoints' in selected;

    if (isValidSelection) {
      formik.setFieldValue('selectedStudent', selected, false);
      formik.setFieldError('selectedStudent', undefined);

      if (Array.isArray(selected)) {
        const allSelected =
          selected.length === studentOptions.length &&
          studentOptions.every((option) => selected.some((s) => s.value === option.value));
        formik.setFieldValue('selectAll', allSelected, false);
      } else {
        formik.setFieldValue('selectAll', false, false);
      }

      formik.setFieldTouched('selectedStudent', true, false);
    } else {
      formik.setFieldValue('selectedStudent', null, false);
      formik.setFieldError('selectedStudent', 'Invalid student selection');
      formik.setFieldTouched('selectedStudent', true, false);
    }
  };

  const handleReasonChange = (selectedReason: any) => {
    formik.setFieldValue('reason', selectedReason);

    if (selectedReason && selectedReason.priceType === 'fixed') {
      formik.setFieldValue('amount', selectedReason.points.toString());
      formik.setFieldTouched('amount', false);
    } else {
      formik.setFieldValue('amount', '');
    }
  };

  const selectedStudentsList = React.useMemo(() => {
    if (formik.values.selectAll) return studentOptions;
    if (!formik.values.selectedStudent) return [];
    if (Array.isArray(formik.values.selectedStudent)) return formik.values.selectedStudent;
    return [formik.values.selectedStudent];
  }, [formik.values.selectedStudent, formik.values.selectAll, studentOptions]);

  const currentPoints = React.useMemo(() => {
    if (selectedStudentsList.length === 1) {
      return selectedStudentsList[0].currentPoints;
    }
    return selectedStudentsList.reduce((sum, s) => sum + s.currentPoints, 0);
  }, [selectedStudentsList]);

  const transactionAmount = React.useMemo(() => Number(formik.values.amount) || 0, [formik.values.amount]);

  const selectedTransactionType = React.useMemo(() => {
    if (!formik.values.reason?.value) return null;
    return reasons.find((reason) => reason.id === formik.values.reason?.value);
  }, [formik.values.reason, reasons]);

  const isCredit = selectedTransactionType?.type === 'credit';
  const isDebit = selectedTransactionType?.type === 'debit';
  const isFixed = selectedTransactionType?.priceType === 'fixed';

  // Calculate remaining points
  const remainingPoints = React.useMemo(() => {
    if (!selectedTransactionType) return currentPoints;

    if (isDebit) {
      const remaining = currentPoints - transactionAmount;
      return remaining < 0 ? remaining - 35 : remaining;
    } else if (isCredit) {
      return currentPoints + transactionAmount;
    }

    return currentPoints;
  }, [currentPoints, transactionAmount, selectedTransactionType, isCredit, isDebit]);

  const willOverdraw = isDebit && selectedStudentsList.length === 1 && transactionAmount > currentPoints;

  const handleClose = React.useCallback(() => {
    if (!isSubmitting) {
      formik.resetForm();
      closeModal();
    }
  }, [isSubmitting, closeModal, formik]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 580, md: 660, lg: 700 },
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.14)',
        },
      }}
    >
      <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Sticky Header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: 'rgba(37, 99, 235, 0.1)',
                color: '#2563EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon icon="solar:wallet-money-bold-duotone" width={20} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem', lineHeight: 1.2 }}>
                Create New Transaction
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                Issue earnings, classroom rewards, or store debit deductions.
              </Typography>
            </Box>
          </Stack>

          <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary', p: 0.75 }}>
            <IoClose size={18} />
          </IconButton>
        </Box>

        {/* Scrollable Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, bgcolor: '#FAFAFA' }}>
          <Stack spacing={2}>
            {/* Card 1: Recipient Selection */}
            <Card
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: formik.touched.selectedStudent && formik.errors.selectedStudent ? 'error.main' : 'divider',
                boxShadow: 'none',
                bgcolor: 'background.paper',
              }}
            >
              <SelectComponent
                label="Select Recipient"
                options={studentOptions}
                isMulti={!studentId}
                placeholder="Search and select scholars..."
                value={formik.values.selectAll ? studentOptions : formik.values.selectedStudent}
                onChange={handleStudentSelection}
                // @ts-ignore
                error={Boolean(formik.touched.selectedStudent && formik.errors.selectedStudent)}
                helperText={
                  formik.touched.selectedStudent && typeof formik.errors.selectedStudent === 'string'
                    ? formik.errors.selectedStudent
                    : undefined
                }
                isLoading={isLoadingStudents}
                isDisabled={false}
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
              />

              {/* Single Scholar Active Preview */}
              {selectedStudentsList.length === 1 && (
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                      }}
                    >
                      {selectedStudentsList[0].label.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78125rem' }}>
                        {selectedStudentsList[0].label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                        Current Wallet Balance: ${selectedStudentsList[0].currentPoints.toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={`$${selectedStudentsList[0].currentPoints.toFixed(2)}`}
                    size="small"
                    sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 700, bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#059669' }}
                  />
                </Box>
              )}
            </Card>

            {/* Card 2: Transaction Reason & Category */}
            <Card
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: formik.touched.reason && formik.errors.reason ? 'error.main' : 'divider',
                boxShadow: 'none',
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  2. What is it for? *
                </Typography>

                {/* Filter capsule chips */}
                <Stack direction="row" spacing={0.5}>
                  <Chip
                    label="All"
                    size="small"
                    onClick={() => setTypeFilter('ALL')}
                    sx={{
                      height: 22,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: typeFilter === 'ALL' ? 'primary.main' : 'grey.100',
                      color: typeFilter === 'ALL' ? '#FFFFFF' : 'text.primary',
                    }}
                  />
                  <Chip
                    icon={<Icon icon="solar:arrow-down-left-bold" width={12} />}
                    label="Money in (+)"
                    size="small"
                    onClick={() => setTypeFilter('credit')}
                    sx={{
                      height: 22,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: typeFilter === 'credit' ? '#059669' : 'grey.100',
                      color: typeFilter === 'credit' ? '#FFFFFF' : '#059669',
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                    }}
                  />
                  <Chip
                    icon={<Icon icon="solar:arrow-up-right-bold" width={12} />}
                    label="Money out (−)"
                    size="small"
                    onClick={() => setTypeFilter('debit')}
                    sx={{
                      height: 22,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: typeFilter === 'debit' ? '#DC2626' : 'grey.100',
                      color: typeFilter === 'debit' ? '#FFFFFF' : '#DC2626',
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                    }}
                  />
                </Stack>
              </Stack>

              <SelectComponent
                label="Transaction Reason"
                labelStyle={{ display: 'none' }}
                placeholder="Pick a job, bill or fine..."
                value={formik.values.reason}
                onChange={handleReasonChange}
                options={reasonOptions}
                formatOptionLabel={(option: any) => (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Box sx={{ color: option.type === 'credit' ? '#059669' : '#DC2626', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Icon
                          icon={option.type === 'credit' ? 'solar:arrow-down-left-bold' : 'solar:arrow-up-right-bold'}
                          width={14}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.2 }}>
                        {option.name || option.label}
                        {option.type === 'debit' && option.chargeMode === 'bill' ? ' · Bill' : ''}
                      </Typography>
                    </Stack>
                    <Chip
                      label={option.priceType === 'fixed' ? `${option.type === 'credit' ? '+' : '-'}$${option.points}` : 'Any amount'}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        bgcolor: option.type === 'credit' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: option.type === 'credit' ? '#059669' : '#DC2626',
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                )}
                // @ts-ignore
                error={Boolean(formik.touched.reason && formik.errors.reason)}
                helperText={formik.touched.reason && formik.errors.reason}
                disabled={isLoadingReasons}
              />

              {/* Selected Reason Preview */}
              {selectedTransactionType && (
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: isCredit ? 'rgba(16, 185, 129, 0.06)' : 'rgba(239, 68, 68, 0.06)',
                    border: '1px solid',
                    borderColor: isCredit ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ color: isCredit ? '#059669' : '#DC2626', display: 'flex', alignItems: 'center' }}>
                      <Icon
                        icon={isCredit ? 'solar:arrow-down-left-bold' : 'solar:arrow-up-right-bold'}
                        width={18}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.78125rem' }}>
                      {selectedTransactionType.name}
                    </Typography>
                  </Stack>
                  <Chip
                    label={isFixed ? `Set price $${selectedTransactionType.points}` : 'Any amount'}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      bgcolor: isCredit ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                      color: isCredit ? '#059669' : '#DC2626',
                    }}
                  />
                </Box>
              )}
            </Card>

            {/* Card 3: Amount & Memo */}
            <Card
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: formik.touched.amount && formik.errors.amount ? 'error.main' : 'divider',
                boxShadow: 'none',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 1 }}>
                3. Amount & Notes *
              </Typography>

              <MuiTextField
                fullWidth
                size="small"
                label="Amount (Points)"
                placeholder="0.00"
                value={formik.values.amount}
                onChange={formik.handleChange}
                name="amount"
                error={Boolean(formik.touched.amount && formik.errors.amount)}
                helperText={
                  isFixed
                    ? 'Standard fixed price applied for this category'
                    : formik.touched.amount && formik.errors.amount
                }
                disabled={isSubmitting || isFixed}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary', fontWeight: 600, fontSize: '0.875rem' }}>$</Typography>,
                  sx: { borderRadius: 1.5, fontSize: '0.875rem', height: 40 },
                }}
              />

              {/* Quick Presets for Variable Pricing */}
              {!isFixed && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.6875rem' }}>
                    Presets:
                  </Typography>
                  {[5, 10, 25, 50, 100].map((preset) => (
                    <Chip
                      key={preset}
                      label={`+$${preset}`}
                      size="small"
                      onClick={() => {
                        formik.setFieldValue('amount', String(preset));
                        formik.setFieldTouched('amount', true, false);
                      }}
                      sx={{
                        height: 22,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: formik.values.amount === String(preset) ? 'primary.light' : 'grey.100',
                        color: formik.values.amount === String(preset) ? 'primary.dark' : 'text.primary',
                        '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' },
                      }}
                    />
                  ))}
                </Stack>
              )}

              {/* Note / Memo */}
              <Box sx={{ mt: 1.5 }}>
                <MuiTextField
                  fullWidth
                  size="small"
                  label="Teacher Note / Memo (Optional)"
                  placeholder="e.g. Excellent presentation on chapter 4, lab clean-up contribution"
                  value={formik.values.description || ''}
                  onChange={formik.handleChange}
                  name="description"
                  multiline
                  rows={2}
                  error={Boolean(formik.touched.description && formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  InputProps={{
                    sx: { borderRadius: 1.5, fontSize: '0.8125rem' },
                  }}
                />
              </Box>
            </Card>

            {/* Card 4: Live Ledger Impact Flow */}
            {selectedStudentsList.length > 0 && selectedTransactionType && transactionAmount > 0 && (
              <Card
                sx={{
                  p: 1.75,
                  borderRadius: 2,
                  bgcolor: isCredit ? 'rgba(16, 185, 129, 0.04)' : 'rgba(239, 68, 68, 0.04)',
                  border: '1px solid',
                  borderColor: isCredit ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                  boxShadow: 'none',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 1 }}>
                  Live Impact Flow
                </Typography>

                <Stack spacing={0.75}>
                  {selectedStudentsList.length === 1 ? (
                    <>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          Current Balance
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.75rem' }}>
                          ${currentPoints.toFixed(2)}
                        </Typography>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ color: isCredit ? '#059669' : '#DC2626', fontSize: '0.75rem', fontWeight: 600 }}>
                          {selectedTransactionType.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: isCredit ? '#059669' : '#DC2626', fontSize: '0.75rem' }}>
                          {isCredit ? '+' : '-'}${transactionAmount.toFixed(2)}
                        </Typography>
                      </Stack>

                      {willOverdraw && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" sx={{ color: '#DC2626', fontSize: '0.75rem', fontWeight: 600 }}>
                            Overdraft Fee
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#DC2626', fontSize: '0.75rem' }}>
                            -$35.00
                          </Typography>
                        </Stack>
                      )}

                      <Divider sx={{ my: 0.25 }} />

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
                          Estimated New Balance
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: remainingPoints < 0 ? '#DC2626' : '#059669',
                            fontSize: '0.9375rem',
                          }}
                        >
                          ${remainingPoints.toFixed(2)}
                        </Typography>
                      </Stack>
                    </>
                  ) : (
                    <>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          Selected Recipients
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.75rem' }}>
                          {selectedStudentsList.length} scholars
                        </Typography>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          Per-Scholar Impact
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: isCredit ? '#059669' : '#DC2626', fontSize: '0.75rem' }}>
                          {isCredit ? '+' : '-'}${transactionAmount.toFixed(2)}
                        </Typography>
                      </Stack>

                      <Divider sx={{ my: 0.25 }} />

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
                          Total Batch Volume
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isCredit ? '#059669' : '#DC2626', fontSize: '0.9375rem' }}>
                          {isCredit ? '+' : '-'}${(transactionAmount * selectedStudentsList.length).toFixed(2)}
                        </Typography>
                      </Stack>
                    </>
                  )}
                </Stack>

                {willOverdraw && (
                  <Alert
                    severity="warning"
                    icon={<Icon icon="solar:danger-triangle-bold-duotone" width={16} />}
                    sx={{ mt: 1, p: 0.75, borderRadius: 1.5, '& .MuiAlert-message': { fontSize: '0.6875rem' } }}
                  >
                    Debit exceeds wallet balance. A $35 overdraw fee will be added.
                  </Alert>
                )}
              </Card>
            )}
          </Stack>
        </Box>

        {/* Sticky Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', fontSize: '0.75rem' }}>
              {selectedStudentsList.length} scholar(s) selected
            </Typography>
            {transactionAmount > 0 && selectedTransactionType && (
              <Typography variant="caption" sx={{ color: isCredit ? '#059669' : '#DC2626', fontWeight: 700, fontSize: '0.75rem' }}>
                {isCredit ? 'Total Credit: +$' : 'Total Debit: -$'}${(transactionAmount * Math.max(1, selectedStudentsList.length)).toFixed(2)}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'none', fontSize: '0.8125rem' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Icon icon="solar:check-circle-bold-duotone" width={16} />}
              sx={{
                fontWeight: 600,
                borderRadius: 1.5,
                textTransform: 'none',
                px: 2.25,
                fontSize: '0.8125rem',
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Transaction'}
            </Button>
          </Stack>
        </Box>
      </form>
    </Drawer>
  );
};

export default TransactionModal;
